import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { useNavigate, useLocation } from 'react-router-dom';
import { theme } from '../../../styles/theme';
import { scrollToWebsiteSection } from '../../common/ScrollLink';
import { useWebsiteAdminAuth } from '../../../context/WebsiteAdminAuthContext';
import { WebsiteStorefrontPrimaryButton } from '../../common/websiteSection';
import NeatScheduleMark from '../../../../shared/components/brand/NeatScheduleMark';

const MobileHeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background: rgba(255, 255, 255, 0.97);
  backdrop-filter: blur(12px);
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  padding-top: calc(${theme.spacing.sm} + env(safe-area-inset-top, 0px));
  border-bottom: 1px solid var(--login-form-border, ${theme.colors.secondary.dark});
`;

const Nav = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  flex-direction: row-reverse;
`;

const HamburgerButton = styled.button`
  background: none;
  border: none;
  width: 40px;
  height: 40px;
  padding: ${theme.spacing.xs};
  cursor: pointer;
  z-index: 101;
  position: relative;
  border-radius: ${theme.borderRadius.full};
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${theme.colors.secondary.main};
  }

  &:active {
    background: ${theme.colors.secondary.dark};
    transform: scale(0.95);
  }

  span {
    display: block;
    width: 22px;
    height: 2px;
    background-color: ${props => props.isOpen ? 'transparent' : theme.colors.text.primary};
    position: relative;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    &::before,
    &::after {
      content: '';
      position: absolute;
      width: 22px;
      height: 2px;
      background-color: ${theme.colors.text.primary};
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      border-radius: 2px;
      left: 0;
    }

    &::before {
      top: ${props => props.isOpen ? '0' : '-7px'};
      transform: ${props => props.isOpen ? 'rotate(45deg)' : 'none'};
      transform-origin: center;
      opacity: ${props => props.isOpen ? '1' : '0.95'};
    }

    &::after {
      bottom: ${props => props.isOpen ? '0' : '-7px'};
      transform: ${props => props.isOpen ? 'rotate(-45deg)' : 'none'};
      transform-origin: center;
      opacity: ${props => props.isOpen ? '1' : '0.95'};
    }
  }

  &:hover span {
    &::before {
      top: ${props => props.isOpen ? '0' : '-8px'};
      opacity: 1;
    }
    &::after {
      bottom: ${props => props.isOpen ? '0' : '-8px'};
      opacity: 1;
    }
  }
`;

const MobileMenu = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 85%;
  max-width: 340px;
  background: ${theme.colors.background.primary};
  padding: calc(${theme.spacing.sm} + env(safe-area-inset-top)) ${theme.spacing.lg} calc(${theme.spacing['2xl']} + env(safe-area-inset-bottom));
  transform: translateX(${props => props.isOpen ? '0' : '100%'});
  transition: transform 0.3s ease;
  box-shadow: ${theme.shadows.xl};
  z-index: 100;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
  border-left: 1px solid ${theme.colors.secondary.dark};
  overflow-y: auto;
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
  z-index: 99;
`;

const MobileNavLink = styled.a`
  color: ${theme.colors.text.primary};
  text-decoration: none;
  font-size: ${theme.typography.fontSize.sm};
  font-weight: 500;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  width: 100%;
  display: block;
  background: ${theme.colors.secondary.main};
  border: none;
  text-align: center;
  cursor: pointer;
  font-family: inherit;
  border-radius: ${theme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.sm};
  transition: background-color 0.2s ease, color 0.2s ease;

  &:hover {
    color: ${theme.colors.primary.main};
    background: ${theme.colors.secondary.dark};
  }
`;

const MobilePrimaryButton = styled(WebsiteStorefrontPrimaryButton)`
  width: 100%;
`;

const Button = styled.button`
  background: ${props => {
    if (props.variant === 'secondary') return 'transparent';
    if (props.variant === 'danger') return '#dc2626';
    return theme.colors.primary.main;
  }};
  color: ${props => props.variant === 'secondary' ? theme.colors.primary.main : 'white'};
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.md};
  border: ${props => props.variant === 'secondary' ? `1px solid ${theme.colors.primary.main}` : 'none'};
  font-weight: 600;
  font-size: ${theme.typography.fontSize.sm};
  width: 100%;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover:not(:disabled) {
    background: ${props => {
      if (props.variant === 'secondary') return theme.colors.secondary.main;
      if (props.variant === 'danger') return '#b91c1c';
      return theme.colors.primary.dark;
    }};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: ${theme.colors.text.primary};
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;

  &:hover {
    opacity: 0.8;
  }
`;

const MenuHeaderBar = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${theme.spacing.md};
  padding: ${theme.spacing.xs} 0;
`;

const MenuTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  font-weight: 700;
  color: ${theme.colors.text.primary};
`;

const MenuSectionTitle = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.light};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin: ${theme.spacing.sm} 0 ${theme.spacing.xs};
`;

const MenuDivider = styled.div`
  height: 1px;
  width: 100%;
  background: ${theme.colors.secondary.dark};
  margin: ${theme.spacing.sm} 0;
`;

const ActionButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;


const MobileHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { adminUser, isSpecialAdmin, adminLogout } = useWebsiteAdminAuth();

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  const handleScroll = (sectionId) => {
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollToId: sectionId } });
      setIsMenuOpen(false);
      return;
    }

    if (scrollToWebsiteSection(sectionId)) {
      window.history.replaceState(null, '', `#${sectionId}`);
      setIsMenuOpen(false);
    }
  };

  const handlePageNavigation = (path) => {
    navigate(path);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    setIsMenuOpen(false);
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    closeMenu();
  };

  const handleAuthAction = () => {
    navigate('/app/login');
    setIsMenuOpen(false);
  };

  const handleAdminLogout = async () => {
    try {
      await adminLogout();
      setIsMenuOpen(false);
      navigate('/');
    } catch (error) {
      console.error('Error logging out of admin:', error);
    }
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  return (
    <>
      <MobileHeaderContainer>
        <Nav>
          <LogoContainer>
            <div onClick={() => {
              navigate('/');
              scrollToTop();
            }} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <NeatScheduleMark mark="header" className="neatScheduleMark--compact" />
            </div>
          </LogoContainer>

          <HamburgerButton
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            isOpen={isMenuOpen}
            aria-label="Toggle menu"
          >
            <span />
          </HamburgerButton>
        </Nav>
      </MobileHeaderContainer>

      <Overlay isOpen={isMenuOpen} onClick={closeMenu} />

      <MobileMenu isOpen={isMenuOpen}>
        <MenuHeaderBar>
          <MenuTitle>
            <NeatScheduleMark mark="header" className="neatScheduleMark--compact" />
            <span>Menu</span>
          </MenuTitle>
          <CloseButton onClick={closeMenu} aria-label="Close menu">×</CloseButton>
        </MenuHeaderBar>

        <MenuSectionTitle>Explore</MenuSectionTitle>
        <MobileNavLink as="button" onClick={() => handlePageNavigation('/why')}>Why Us</MobileNavLink>
        <MobileNavLink as="button" onClick={() => handleScroll('features')}>Features</MobileNavLink>
        <MobileNavLink as="button" onClick={() => handleScroll('pricing')}>Pricing</MobileNavLink>
        <MobileNavLink as="button" onClick={() => handleScroll('support')}>Support</MobileNavLink>
        <MenuDivider />

        <ActionButtons>
          {(adminUser && isSpecialAdmin) ? (
            <>
              <MobilePrimaryButton type="button" onClick={() => { navigate('/app/login'); closeMenu(); }}>
                Open App
              </MobilePrimaryButton>
              <Button variant="danger" onClick={handleAdminLogout}>
                Sign Out
              </Button>
            </>
          ) : (
            <MobilePrimaryButton type="button" onClick={handleAuthAction}>
              Log in
            </MobilePrimaryButton>
          )}
        </ActionButtons>
      </MobileMenu>
    </>
  );
};

export default MobileHeader;
